import type { LimbBox } from "./hitbox.js";

/**
 * The animals in the valley, as content. PASSIVE — they never attack, and the
 * only thing they do about a person is leave — but a deer breaking cover puts
 * birds up, and every scav in earshot of the shot knows somebody is out there.
 * DATA ONLY: the herd behaviour, the flushing and the carcasses live
 * server-side. Three DIFFERENT hunts rather than one at three sizes:
 *
 *   hare     8 hp, sees 25 m, bolts 25 m at 9 m/s and then FREEZES flat.
 *   deer    45 hp, sees 45 m, runs 70 m and keeps going; flushes birds.
 *   chamois 55 hp, sees 50 m, runs 80 m, lives above the treeline; the trophy.
 */

export type WildlifeSpecies = "hare" | "deer" | "chamois";

/** Every species, in the order a menu or a quest log lists them. */
export const WILDLIFE_SPECIES: readonly WildlifeSpecies[] = ["hare", "deer", "chamois"];

/** Where a species lives; the server maps these onto terrain `land` labels. */
export type Habitat = "open" | "edge" | "high";

/**
 * How far a prey animal sees AROUND itself, as a cosine: 150° each side of the
 * nose, a 60° blind wedge astern — why you stalk from behind. A cosine so the
 * check is one dot product.
 */
const PREY_FOV_HALF_COS = Math.cos((150 * Math.PI) / 180);

export interface SpeciesDef {
  id: WildlifeSpecies;
  /** English, singular, as a quest log names it. */
  name: string;
  /** ...and plural, which is not always a different word. */
  namePlural: string;
  hp: number;
  /** Damage multiplier for a `"head"` limb hit. */
  headMult: number;
  /** Grazing/ambling speed, m/s. */
  walkMps: number;
  /** Flat-out flight speed, m/s. */
  runMps: number;
  /** How far it can make a person out, metres. */
  sightRangeM: number;
  /** Cosine of the half-angle of its vision cone. See `PREY_FOV_HALF_COS`. */
  fovHalfCos: number;
  /** Multiplies the scav footstep-hearing radius: prey listens harder. */
  hearingMult: number;
  /** How far it runs once it is spooked, metres. */
  fleeDistM: number;
  /** It goes to ground at the end of its dash instead of staying up. */
  hidesAfterFlee: boolean;
  /** Breaking cover puts birds up — a `birdFlush` event, and a noise scavs hear. */
  flushesBirds: boolean;
  groupMin: number;
  groupMax: number;
  habitat: Habitat;
  /** Collision capsule, the same convention as a player's. */
  capsule: { radius: number; height: number };
  /** Where its eyes are, metres above its feet — the origin of its sight test. */
  eyeHeight: number;
  /**
   * Hitboxes in ENTITY-LOCAL space, feet at origin, facing −Z. Only `"thorax"`
   * and `"head"` are used — an animal has a pool of hit points and a head worth
   * aiming at — which lets it ride the EXISTING hit path without a second
   * `LimbId` union.
   */
  boxes: readonly LimbBox[];
  /** What the carcass yields, dropped on the ground beside it. */
  drops: readonly { defId: string; count: number }[];
}

function box(
  limb: "thorax" | "head",
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
): LimbBox {
  return {
    limb,
    min: Object.freeze({ x: minX, y: minY, z: minZ }),
    max: Object.freeze({ x: maxX, y: maxY, z: maxZ }),
  };
}

/** Frozen for the same reason `hitbox.ts` freezes its own: these are handed
 *  straight to a renderer and to hit registration, and neither may scribble. */
function boxes(...list: LimbBox[]): readonly LimbBox[] {
  for (const b of list) Object.freeze(b);
  return Object.freeze(list);
}

/**
 * The roster, frozen and shared — hit registration reads `boxes` off it once
 * per animal per shot.
 */
export const SPECIES: Readonly<Record<WildlifeSpecies, SpeciesDef>> = Object.freeze({
  /**
   * Small, fast, and the only animal that hides rather than runs: a SEEING
   * problem, not a damage problem. Bolts at 9 m/s for twenty-five metres, then
   * flattens and stops existing.
   */
  hare: Object.freeze({
    id: "hare",
    name: "Hare",
    namePlural: "hares",
    hp: 8,
    headMult: 2.5,
    walkMps: 1.2,
    runMps: 9,
    sightRangeM: 25,
    fovHalfCos: PREY_FOV_HALF_COS,
    hearingMult: 1.5,
    fleeDistM: 25,
    hidesAfterFlee: true,
    // Nothing takes off over a hare: it goes flat, which is the whole trick.
    flushesBirds: false,
    groupMin: 1,
    groupMax: 2,
    habitat: "open",
    capsule: Object.freeze({ radius: 0.2, height: 0.35 }),
    eyeHeight: 0.3,
    boxes: boxes(
      box("thorax", -0.12, 0.05, -0.22, 0.12, 0.3, 0.18),
      box("head", -0.08, 0.15, -0.36, 0.08, 0.38, -0.2),
    ),
    drops: Object.freeze([Object.freeze({ defId: "meat_hare", count: 1 })]),
  }),
  /**
   * Roe deer: the animal the valley is full of, and the loudest to shoot at — a
   * wounded one covers seventy metres, and it flushes birds when it breaks,
   * announcing the hunter before a shot.
   */
  deer: Object.freeze({
    id: "deer",
    name: "Roe deer",
    // "Deers" is not a word, and a quest log that prints one is a quest log
    // nobody in the valley wrote.
    namePlural: "roe deer",
    hp: 45,
    headMult: 2.5,
    walkMps: 1.4,
    runMps: 8.5,
    sightRangeM: 45,
    fovHalfCos: PREY_FOV_HALF_COS,
    hearingMult: 1.8,
    fleeDistM: 70,
    hidesAfterFlee: false,
    flushesBirds: true,
    groupMin: 2,
    groupMax: 3,
    habitat: "edge",
    capsule: Object.freeze({ radius: 0.35, height: 1.3 }),
    eyeHeight: 1.1,
    boxes: boxes(
      box("thorax", -0.22, 0.55, -0.55, 0.22, 1.05, 0.55),
      // Neck and head as one block — the part that shows over the grass.
      box("head", -0.12, 0.95, -0.95, 0.12, 1.35, -0.55),
    ),
    drops: Object.freeze([Object.freeze({ defId: "meat_venison", count: 2 })]),
  }),
  /**
   * Chamois: the high-ground animal, toughest and wariest, the one that wants a
   * scope and a long approach — and the only one that drops something other
   * than meat.
   */
  chamois: Object.freeze({
    id: "chamois",
    name: "Chamois",
    // Same in both numbers, like sheep.
    namePlural: "chamois",
    hp: 55,
    headMult: 2.5,
    walkMps: 1.3,
    runMps: 7.5,
    sightRangeM: 50,
    fovHalfCos: PREY_FOV_HALF_COS,
    hearingMult: 1.6,
    fleeDistM: 80,
    hidesAfterFlee: false,
    flushesBirds: true,
    groupMin: 2,
    groupMax: 4,
    habitat: "high",
    capsule: Object.freeze({ radius: 0.35, height: 1.1 }),
    eyeHeight: 0.95,
    boxes: boxes(
      box("thorax", -0.22, 0.45, -0.5, 0.22, 0.95, 0.5),
      box("head", -0.12, 0.8, -0.85, 0.12, 1.15, -0.5),
    ),
    drops: Object.freeze([
      Object.freeze({ defId: "meat_venison", count: 2 }),
      Object.freeze({ defId: "gamskrickel", count: 1 }),
    ]),
  }),
});

/**
 * The definition for a species id. THROWS rather than returning `null`, unlike
 * `getItemDef`: a `WildlifeSpecies` is a compile-time literal, so anything else
 * is a caller bug.
 */
export function speciesDef(id: WildlifeSpecies): SpeciesDef {
  const def = SPECIES[id];
  if (def === undefined) throw new Error(`unknown wildlife species "${String(id)}"`);
  return def;
}
